import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { X, Bell, CheckCircle, AlertCircle, Info, XCircle } from 'lucide-react';
import { addRealTimeNotification, markAsRead } from '../store/slices/notificationSlice';

const RealTimeNotification = () => {
  const dispatch = useDispatch();
  const { notifications } = useSelector((state) => state.notifications);
  const [showNotifications, setShowNotifications] = useState(true);
  const [notificationQueue, setNotificationQueue] = useState([]);

  // Listen for new notifications from socket
  useEffect(() => {
    const handleNewNotification = (event) => {
      const notification = event.detail;
      setNotificationQueue(prev => [...prev, notification]);
      
      // Auto-remove notification after 5 seconds
      setTimeout(() => {
        setNotificationQueue(prev => prev.filter(n => n._id !== notification._id));
      }, 5000);
    };

    window.addEventListener('newNotification', handleNewNotification);
    return () => window.removeEventListener('newNotification', handleNewNotification);
  }, []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'Booking':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'Payment':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'Overdue':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'Salary':
        return <CheckCircle className="w-5 h-5 text-yellow-500" />;
      case 'Scholarship':
        return <Info className="w-5 h-5 text-purple-500" />;
      case 'Transfer':
        return <Info className="w-5 h-5 text-indigo-500" />;
      case 'General Transaction':
        return <Info className="w-5 h-5 text-gray-500" />;
      case 'Society Creation':
        return <Info className="w-5 h-5 text-emerald-500" />;
      case 'System':
        return <Info className="w-5 h-5 text-cyan-500" />;
      case 'Resell':
        return <Info className="w-5 h-5 text-orange-500" />;
      case 'Message':
        return <Bell className="w-5 h-5 text-blue-500" />;
      case 'GroupMessage':
        return <Bell className="w-5 h-5 text-purple-500" />;
      case 'MessageRead':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'MessageDelivery':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationStyle = (type) => {
    switch (type) {
      case 'Booking':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'Payment':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'Overdue':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'Salary':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'Scholarship':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'Transfer':
        return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case 'General Transaction':
        return 'bg-gray-50 border-gray-200 text-gray-800';
      case 'Society Creation':
        return 'bg-emerald-50 border-emerald-200 text-emerald-800';
      case 'System':
        return 'bg-cyan-50 border-cyan-200 text-cyan-800';
      case 'Resell':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'Message':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'GroupMessage':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'MessageRead':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'MessageDelivery':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const handleMarkAsRead = (notificationId) => {
    dispatch(markAsRead(notificationId));
  };

  const handleDismiss = (notificationId) => {
    setNotificationQueue(prev => prev.filter(n => n._id !== notificationId));
  };

  if (!showNotifications || notificationQueue.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {notificationQueue.map((notification) => (
        <div
          key={notification._id}
          className={`
            ${getNotificationStyle(notification.type)}
            border rounded-lg p-4 shadow-lg backdrop-blur-sm
            transform transition-all duration-300 ease-out
            animate-slideInFromRight
            hover:scale-105
          `}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-0.5">
              {getNotificationIcon(notification.type)}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  {notification.type}
                </p>
                <div className="flex items-center space-x-1">
                  {!notification.isRead && (
                    <button
                      onClick={() => handleMarkAsRead(notification._id)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDismiss(notification._id)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <p className="text-sm mt-1 break-words">
                {notification.message}
              </p>
              
              <p className="text-xs text-gray-500 mt-2">
                {new Date(notification.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RealTimeNotification; 